import React, { useEffect, useState } from 'react'

import { Link } from 'gatsby'
import styled from 'styled-components'

import {
  Paragraph,
  ParagraphSmall,
  TitleXL,
  TitleH3,
  TitleH4
} from 'src/components/blocks/Typography'
import { captureEvents } from 'src/utilities/analyticsEvents'
import { Breakpoints } from 'src/utilities/Breakpoints'
import { BrandColors, BrandTokens } from 'src/utilities/Colors'
import { FontFamily } from 'src/utilities/Type'
import { getUtmParam, utmParamsList } from 'src/utilities/utmUtils'

const LoaderSvg = ({
  fillColor = BrandColors.primary,
  strokeColor = 'none',
  size = '24px'
}: {
  fillColor?: string
  strokeColor?: string
  size?: string
}) => (
  <svg
    fill={fillColor}
    height='24'
    stroke={strokeColor}
    style={{
      display: 'block',
      margin: '6rem auto',
      width: size,
      height: size
    }}
    viewBox='0 0 24 24'
    width='24'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect width='9' height='9' x='1.5' y='1.5' rx='1'>
      <animate
        id='spinner_M16P'
        attributeName='x'
        begin='0;spinner_wNI2.end+0.15s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='1.5;.5;1.5'
      ></animate>
      <animate
        attributeName='y'
        begin='0;spinner_wNI2.end+0.15s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='1.5;.5;1.5'
      ></animate>
      <animate
        attributeName='width'
        begin='0;spinner_wNI2.end+0.15s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='9;11;9'
      ></animate>
      <animate
        attributeName='height'
        begin='0;spinner_wNI2.end+0.15s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='9;11;9'
      ></animate>
    </rect>
    <rect width='9' height='9' x='13.5' y='1.5' rx='1'>
      <animate
        attributeName='x'
        begin='spinner_M16P.begin+0.15s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='13.5;12.5;13.5'
      ></animate>
      <animate
        attributeName='y'
        begin='spinner_M16P.begin+0.15s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='1.5;.5;1.5'
      ></animate>
      <animate
        attributeName='width'
        begin='spinner_M16P.begin+0.15s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='9;11;9'
      ></animate>
      <animate
        attributeName='height'
        begin='spinner_M16P.begin+0.15s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='9;11;9'
      ></animate>
    </rect>
    <rect width='9' height='9' x='13.5' y='13.5' rx='1'>
      <animate
        attributeName='x'
        begin='spinner_M16P.begin+0.3s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='13.5;12.5;13.5'
      ></animate>
      <animate
        attributeName='y'
        begin='spinner_M16P.begin+0.3s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='13.5;12.5;13.5'
      ></animate>
      <animate
        attributeName='width'
        begin='spinner_M16P.begin+0.3s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='9;11;9'
      ></animate>
      <animate
        attributeName='height'
        begin='spinner_M16P.begin+0.3s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='9;11;9'
      ></animate>
    </rect>
    <rect width='9' height='9' x='1.5' y='13.5' rx='1'>
      <animate
        id='spinner_wNI2'
        attributeName='x'
        begin='spinner_M16P.begin+0.45s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='1.5;.5;1.5'
      ></animate>
      <animate
        attributeName='y'
        begin='spinner_M16P.begin+0.45s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='13.5;12.5;13.5'
      ></animate>
      <animate
        attributeName='width'
        begin='spinner_M16P.begin+0.45s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='9;11;9'
      ></animate>
      <animate
        attributeName='height'
        begin='spinner_M16P.begin+0.45s'
        dur='0.6s'
        keyTimes='0;.2;1'
        values='9;11;9'
      ></animate>
    </rect>
  </svg>
)

const FormTitle = styled(TitleH4)<{
  theme?: 'light' | 'dark'
}>`
  em {
    color: ${props =>
      props.theme === 'light' ? BrandColors.primary : BrandColors.electric100};
    font-family: ${FontFamily.brandPrimary};
  }
`

const LogoLink = styled(Link)`
  display: inline-block;
  margin-bottom: 1em;
`

const FormWrapper = styled.div<{
  theme?: 'light' | 'dark'
  showLogo?: boolean
  minHeight?: string
}>`
  border: 2px solid ${BrandTokens.backgroundDark};
  box-shadow: ${BrandTokens.boxShadow8};
  padding: 2.5rem;
  margin-top: 2rem;
  background-color: ${props =>
    props.theme === 'light' ? BrandColors.white : BrandColors.midnight700};
  color: ${props =>
    props.theme === 'light' ? BrandColors.text : BrandColors.white};
  position: relative;
  z-index: 2;
  min-width: 300px;
  min-height: ${props => props.minHeight || '620px'};

  @media (min-width: ${Breakpoints.md}px) {
    margin-top: 0;
  }

  ${ParagraphSmall} {
    margin-top: 1rem;
    text-align: center;

    a {
      color: ${props =>
        props.theme === 'light'
          ? BrandColors.primary
          : BrandColors.electric300};
      text-decoration: underline;
      font-weight: 600;
    }
  }

  ${TitleXL} {
    position: relative;
    z-index: 2;
  }
`

const SignUpForm = ({
  cornerBgColor = BrandColors.white,
  cornerFgColor = BrandColors.black,
  errorMsg = 'There was an error signing up. Please try again or contact support.',
  fillUtms = false,
  formMinHeight = '620px',
  formTitle = 'Create your <em>free</em> Flatfile account',
  formTitleAs = 'h2',
  hsFormId = 'fbb84d21-4b0e-4542-a09e-75059d44412f',
  hsPortalId = '4588656',
  hsSource,
  hsSource_version = 'v1',
  showBtnMessage = false,
  showLogo = false,
  thankYouMsg = 'Thanks for signing up with Flatfile! Check your email to activate your new account.',
  theme
}: {
  cornerBgColor?: string
  cornerFgColor?: string
  errorMsg?: string
  fillUtms?: boolean
  formMinHeight?: string
  formTitle?: string
  formTitleAs?: string
  hsFormId: string
  hsPortalId?: string
  hsSource?: string
  hsSource_version?: string
  showBtnMessage?: boolean
  showLogo?: boolean
  thankYouMsg?: string
  theme?: 'light' | 'dark'
}) => {
  const [errorMessage, setErrorMessage] = useState(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [fetchComplete, setFetchComplete] = useState(false)

  const findIntentInPath = (path: string, allowedIntents: string[]): string => {
    for (const intent of allowedIntents) {
      if (path.includes(`/${intent}/`)) {
        return intent
      }
    }
    return 'platform' // default intent
  }

  useEffect(() => {
    const prodDomains = ['flatfile.com', 'www.flatfile.com']

    const isStageDomain = hostname => {
      return /^deploy-preview-\d+--flatfile-retail\.netlify\.app$/.test(
        hostname
      )
    }

    const FRONTEGG_DOMAIN = prodDomains.includes(window.location.hostname)
      ? 'https://identity.flatfile.com'
      : isStageDomain(window.location.hostname)
      ? 'https://app-23vnr2i3qffo.frontegg.com'
      : 'https://app-le5ida0poi1c.frontegg.com'

    const script = document.createElement('script')
    script.src = 'https://js.hsforms.net/forms/embed/v2.js'
    script.async = true
    document.body.appendChild(script)

    script.addEventListener('load', () => {
      const formId = hsFormId
      const portalId = hsPortalId
      const source = hsSource
      const source_version = hsSource_version
      const region = 'na1'
      // const errorMsg =
      //   'There was an error signing up. Please try again or contact support.'
      const allowedIntents = ['mapping', 'portal', 'projects', 'workbooks']
      const intent = findIntentInPath(window.location.pathname, allowedIntents)

      if (window.hbspt) {
        window.hbspt.forms.create({
          formId,
          inlineMessage: `&nbsp;`,

          onFormReady($form) {
            captureEvents({
              posthog: {
                data: {
                  formId,
                  portalId,
                  region,
                  source
                },
                eventName: 'Form ready'
              }
            })

            // Fill in UTM parameters from cookie
            if (fillUtms) {
              utmParamsList.forEach(param => {
                const value = getUtmParam(param)
                const inputElement = $form.querySelector(
                  `input[name="${param}"]`
                )

                if (inputElement) {
                  inputElement.value = value
                }
              })
            }

            // Fill in intent on hidden field on form
            if ($form.querySelector(`input[name="signup_initial_intent"]`)) {
              $form.querySelector(`input[name="signup_initial_intent"]`).value =
                intent
            }

            // Center and full width submit button
            const formButton = $form.querySelector('input[type="submit"]')
            formButton.style.width = '100%'
            formButton.style.borderRadius = '6px'

            if (showBtnMessage) {
              // and add a no credit card required message below the submit button
              const noCreditCardRequired = document.createElement('p')
              noCreditCardRequired.innerHTML = 'No credit card required'
              noCreditCardRequired.style.color = BrandColors.midnight200
              noCreditCardRequired.style.fontWeight = '500'
              noCreditCardRequired.style.fontFamily = 'sans-serif'
              noCreditCardRequired.style.fontSize = '0.875rem'
              noCreditCardRequired.style.letterSpacing = '0.02em'
              noCreditCardRequired.style.textAlign = 'center'
              formButton.parentNode.insertBefore(
                noCreditCardRequired,
                formButton.nextSibling
              )
            }
          },

          onFormSubmit($form) {
            const email = $form.querySelector('input[name="email"]').value
            const firstname = $form.querySelector(
              'input[name="firstname"]'
            ).value
            const lastname = $form.querySelector('input[name="lastname"]').value
            const companyName = $form.querySelector(
              'input[name="company"]'
            ).value
            const jobTitle = $form.querySelector('select[name="job_role__c"]')
              ? $form.querySelector('select[name="job_role__c"]').value
              : ''

            captureEvents({
              posthog: {
                data: {
                  company: companyName,
                  email,
                  firstname,
                  formId,
                  intent,
                  jobTitle,
                  lastname,
                  portalId,
                  region,
                  source,
                  source_version
                },
                eventName: 'Form submitted'
              }
            })

            // Frontegg signup logic
            const myHeaders = new Headers()
            myHeaders.append('Content-Type', 'application/json')

            // Create the data that we'll send to frontegg
            const data = {
              companyName,
              email,
              metadata: JSON.stringify({ productIntent: intent }),
              name: firstname + ' ' + lastname,
              provider: 'local'
            }

            // Construct the response options
            const requestOptions = {
              body: JSON.stringify(data),
              headers: myHeaders,
              method: 'POST',
              redirect: 'follow' as RequestRedirect
            }

            // POST to frontegg with the user information
            fetch(
              `${FRONTEGG_DOMAIN}/identity/resources/users/v1/signUp`,
              requestOptions
            )
              .then(response => {
                setFetchComplete(true)

                if (response.status === 400) {
                  return response.json().then(resData => {
                    if (
                      resData.errors &&
                      resData.errors.includes('User already exists')
                    ) {
                      setErrorMessage('existing user')
                      captureEvents({
                        posthog: {
                          data: {
                            error_message: 'User already exists',
                            error_status: response.status,
                            error_type: 'signup',
                            page_location: source,
                            submitted_email: email
                          },
                          eventName: 'Error Event'
                        }
                      })
                    } else {
                      setErrorMessage(errorMsg)
                    }
                  })
                } else if (!response.ok) {
                  setErrorMessage(errorMsg)

                  captureEvents({
                    posthog: {
                      data: {
                        error_message: response.statusText,
                        error_status: response.status,
                        error_type: 'signup',
                        page_location: source,
                        submitted_email: email
                      },
                      eventName: 'Error Event'
                    }
                  })

                  throw new Error(errorMsg)
                }
                return response.json()
              })
              .then(resData => {
                setFetchComplete(true)

                captureEvents({
                  linkedInConversionID: 4278073,
                  plausibleEventName: 'Platform Signup',
                  posthog: {
                    data: {
                      company: companyName,
                      email,
                      firstname,
                      formId,
                      intent,
                      jobTitle,
                      lastname,
                      portalId,
                      region,
                      source,
                      source_version
                    },
                    eventName: 'signup - frontend',
                    identify: true
                  },
                  uetEventName: 'signup'
                })
              })
              .catch(error => {
                setFetchComplete(true)
                if (!errorMessage) {
                  setErrorMessage(errorMsg)
                }

                captureEvents({
                  plausibleEventName: 'Signup Network Error',
                  posthog: {
                    data: {
                      error_message: error.statusText || 'Network Error',
                      error_status: error.status || 0,
                      error_type: 'signup',
                      page_location: source,
                      submitted_email: email
                    },
                    eventName: 'Error Event'
                  }
                })
              })
          },

          onFormSubmitted($form) {
            setIsSubmitted(true)
          },
          portalId,
          region,
          target: '#hubSpotForm'
        })
      }
    })
  }, [])

  return (
    <FormWrapper theme={theme} minHeight={formMinHeight}>
      {showLogo && (
        <LogoLink to='/'>
          <img
            alt='Flatfile logo'
            src={
              'https://images.ctfassets.net/hjneo4qi4goj/gL6Blz3kTPdZXWknuIDVx/7bb7c73d93b111ed542d2ed426b42fd5/flatfile.svg'
            }
          />
        </LogoLink>
      )}

      {formTitle && (
        <FormTitle
          as='h2'
          color={theme === 'light' ? BrandColors.text : BrandColors.white}
          className='formTitle'
          theme={theme}
          marginBottom='1.5rem'
        >
          <span dangerouslySetInnerHTML={{ __html: formTitle }} />
        </FormTitle>
      )}

      {errorMessage === 'existing user' ? (
        <div className='error'>
          <Paragraph color={BrandColors.danger}>
            User already exists. Please use a different email address and{' '}
            <a
              style={{ textDecoration: 'underline' }}
              href={window.location.href}
            >
              try again
            </a>{' '}
            or{' '}
            <a
              style={{ textDecoration: 'underline' }}
              href='https://platform.flatfile.com/account/login'
            >
              log in
            </a>{' '}
            instead.
          </Paragraph>
        </div>
      ) : (
        <Paragraph color={BrandColors.danger}>{errorMessage}</Paragraph>
      )}

      {!errorMessage && (
        <div id='hubSpotForm'>
          <LoaderSvg
            size='48px'
            fillColor={
              theme === 'light' ? BrandColors.primary : BrandColors.white
            }
          />
        </div>
      )}

      {isSubmitted && !fetchComplete && (
        <LoaderSvg
          size='48px'
          fillColor={
            theme === 'light' ? BrandColors.primary : BrandColors.white
          }
        />
      )}

      {isSubmitted && !errorMessage && fetchComplete && (
        <Paragraph color={BrandColors.success}>{thankYouMsg}</Paragraph>
      )}

      {!isSubmitted && showBtnMessage && (
        <ParagraphSmall
          color={theme === 'light' ? BrandColors.text : BrandColors.white}
        >
          Already have an account?{' '}
          <a href='https://platform.flatfile.com/account/login'>Log in</a>
        </ParagraphSmall>
      )}
    </FormWrapper>
  )
}

export default SignUpForm
